import { useAuth } from '../../context/AuthContext';
import { usePermission, useIsAdmin } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import { ModuleKey } from '../dashboard/registry/types';
import useHasDefaultTargets from '../dashboard/managed/useHasDefaultTargets';

export interface ModuleVisibility {
    /** Gate key → may the signed-in user reach it. Covers module cards and their sub-links. */
    visibility: Record<string, boolean>;
    /** Institution's enabled module set (fail-open), used for the `gatedModule` check. */
    enabledModules: Set<ModuleKey>;
}

/**
 * Resolves every launcher/sidebar gate — module cards and the pages inside them — in one place.
 *
 * Merge (develop -> dynamic institution roles): this hook arrived from the mobile-responsive
 * redesign keyed off `UserType` role booleans (`isInstitutionAdmin`, `isStoreAdmin`, …). That enum
 * no longer describes access: the global `user.type` role was replaced by the permission catalog,
 * so every role boolean below is now the permission key whose RoleTemplate holders equal the old
 * role set. The expressions are lifted verbatim from this branch's pre-merge `Sidebar.tsx`, which
 * is the file the redesign replaced — including the reasoning comments for each place where no
 * single key reproduced a legacy role pair. Only the PLATFORM roles (SystemAdmin, SaaSOperator)
 * stay JWT-driven, because they are cross-tenant and hold no institution permissions.
 *
 * The sidebar (`SidebarNav`) and the /modules launcher both render from this map plus
 * `MODULE_CATALOG`, so a page is reachable from both surfaces or neither. Hooks run
 * unconditionally at the top; every per-card and per-link decision is a plain map lookup.
 */
export const useModuleVisibility = (): ModuleVisibility => {
    const { user, isSystemAdmin } = useAuth();
    const { enabledModules, visibleModules } = useDashboardAccess();

    // SaaSOperator is a PLATFORM role: cross-tenant, minted on the JWT `roles` claim, and holding
    // no institution permission keys at all — so it stays role-driven, exactly as isSystemAdmin
    // does in AuthContext. Split into exact tokens rather than substring-matching the raw claim.
    const isSaaSOperator = !!user && (Array.isArray(user.roles) ? user.roles : String(user.roles ?? '').split(','))
        .map(r => String(r).trim())
        .includes('SaaSOperator');

    const isAdmin = useIsAdmin();
    const isDepartmentAdmin = user?.isDepartmentAdmin === true;

    // Board roles get the module deep-page links; everyone else collapses to a single Overview.
    const isDashboardBoardRole = usePermission(PERMISSIONS.DASHBOARD_VIEW);
    const canManageAllBoards = usePermission(PERMISSIONS.DASHBOARD_LAYOUT_MANAGE_ALL); // D1
    const canManageTeamBoards = usePermission(PERMISSIONS.DASHBOARD_LAYOUT_MANAGE_TEAM); // D1
    // A MANAGE_TEAM-only caller sees "Role defaults" only when he heads at least one department to set a default for.
    const teamHasTargets = useHasDefaultTargets(canManageTeamBoards && !canManageAllBoards);
    // The Accountant holds DASHBOARD_VIEW_ACCOUNTANT instead (DASHBOARD_VIEW also opens the attendance,
    // leave and people boards): its business module boards (accounting, inventory, sales, payroll, expense) stay reachable.
    const hasAccountantBoard = usePermission(PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT);
    const isBusinessBoardRole = isDashboardBoardRole || hasAccountantBoard;

    const canAccessDashboard = usePermission(PERMISSIONS.MENU_DASHBOARD);
    const canAccessAttendance = usePermission(PERMISSIONS.MENU_ATTENDANCE);
    const isAttendanceBoardRole = usePermission(PERMISSIONS.ATTENDANCE_VIEW_ALL);
    // Work sites are geofence CONFIGURATION, keyed to match the backend WorkSiteController re-key
    // (ATTENDANCE_MANAGE gates every write there) and the /attendance/sites PermissionRoute.
    const canManageWorkSites = usePermission(PERMISSIONS.ATTENDANCE_MANAGE);
    const canAccessHolidays = usePermission(PERMISSIONS.MENU_HOLIDAY);
    const canAccessUsers = usePermission(PERMISSIONS.MENU_USERS);
    const canAccessDevices = usePermission(PERMISSIONS.MENU_DEVICES);
    const canAccessApiKeys = usePermission(PERMISSIONS.MENU_API_KEYS);
    const canAccessDepartments = usePermission(PERMISSIONS.MENU_DEPARTMENTS) || isDepartmentAdmin;
    const canAccessStores = usePermission(PERMISSIONS.MENU_STORES);
    const canAccessAccounting = usePermission(PERMISSIONS.MENU_ACCOUNTING);
    // Group-balance reclassification posts a journal entry: same key as manual journal posting.
    const canReclassGroupBalances = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
    const canManageAccountsForOpening = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    // T10 (bank-rec close-out, frontend part 3): the Banking card's own gate, separate from
    // MENU_ACCOUNTING -- BankAccountController/BankReconciliationController both guard on
    // BANK_ACCOUNT_VIEW, not the accounting menu key.
    const canAccessBanking = usePermission(PERMISSIONS.BANK_ACCOUNT_VIEW);
    const canViewFixedAssets = usePermission(PERMISSIONS.FIXED_ASSET_VIEW);
    const canManageFixedAssetSettings = usePermission(PERMISSIONS.FIXED_ASSET_SETTINGS_MANAGE);
    const canAccessFinancing = usePermission(PERMISSIONS.FINANCING_VIEW); // W3-A
    const canAccessCosting = usePermission(PERMISSIONS.COSTING_VIEW); // W3-I
    const canAccessPayroll = usePermission(PERMISSIONS.MENU_PAYROLL);
    const canAccessTax = usePermission(PERMISSIONS.MENU_TAX);
    const canAccessItemManagement = usePermission(PERMISSIONS.MENU_ITEMS);
    const canAccessUom = usePermission(PERMISSIONS.UOM_MANAGE);
    // Report Management (Employee Consumption) was InstitutionAdmin-only; no catalog key maps to
    // exactly that set, so it is keyed off the admin flag directly — same pattern as the
    // /reports/employee-consumption PermissionRoute (adminOnly).
    const canAccessReports = isAdmin;
    // Stocktakes, transfers and low stock: every backend endpoint behind them needs STOCK_MANAGE.
    const canAccessStocktakes = usePermission(PERMISSIONS.STOCK_MANAGE);
    const canAccessLowStock = usePermission(PERMISSIONS.STOCK_MANAGE);
    const canAccessStockTransfers = usePermission(PERMISSIONS.STOCK_MANAGE);
    const canAccessRequisitions = usePermission(PERMISSIONS.MENU_REQUISITIONS);
    const canAccessNotices = usePermission(PERMISSIONS.MENU_NOTICES);
    const canManageNotices = usePermission(PERMISSIONS.NOTICE_MANAGE);
    const canManageReminderRules = usePermission(PERMISSIONS.INSTITUTION_SETTINGS_MANAGE); // W3-S
    const canManageApprovalWorkflows = usePermission(PERMISSIONS.APPROVAL_WORKFLOW_MANAGE); // FW-3A
    const canAccessTasks = usePermission(PERMISSIONS.MENU_TASKS);
    const canAssignTasks = usePermission(PERMISSIONS.TASK_ASSIGN);
    const canAccessInstitutionSettings = usePermission(PERMISSIONS.INSTITUTION_SETTINGS_VIEW) && !isSystemAdmin;
    const canAccessBilling = usePermission(PERMISSIONS.BILLING_VIEW);
    // Group insurance (BLA §99, REV 2 R6): its own ungated module card, keyed on the new
    // INSURANCE_VIEW key alone — deliberately NOT folded into canAccessPayroll (MENU_PAYROLL),
    // so a payroll-disabled tenant still sees the §99 compliance card in the launcher/sidebar.
    const canAccessInsurance = usePermission(PERMISSIONS.INSURANCE_VIEW);

    // Procurement: the legacy sidebar gated Suppliers/Settings/Purchase-Orders/Pending-Approval/
    // Goods-Receipts/Supplier-Bills/Supplier-Payments on (InstitutionAdmin || InstitutionManager)
    // exactly. No single catalog key holds exactly that pair; PURCHASE_ORDER_APPROVE does
    // (InstitutionManager is the only non-wildcard template carrying it), so it stands in for it.
    const isProcurementManager = usePermission(PERMISSIONS.PURCHASE_ORDER_APPROVE);
    // Catalogue was (InstitutionAdmin || StoreAdmin) exactly; GOODS_RECEIPT_MANAGE is the one
    // catalog key StoreAdmin alone (besides the admin wildcard) carries, so it reproduces that pair.
    const canAccessCatalogue = usePermission(PERMISSIONS.GOODS_RECEIPT_MANAGE);
    // The group wrapper was (IA || IM || SA || Accountant); the Accountant submenu had zero items,
    // so the union of the leaves drops only an always-empty header, not a reachable page.
    const canViewSuppliers = usePermission(PERMISSIONS.SUPPLIER_VIEW);
    const canManageSuppliers = usePermission(PERMISSIONS.SUPPLIER_MANAGE);
    // Exactly the /procurement/suppliers route gate, so the link never leads to a denied page.
    const canAccessSuppliers = canViewSuppliers || canManageSuppliers;
    // Each procurement link mirrors the exact PermissionRoute key in App.tsx so the launcher and
    // sidebar never offer a page that would bounce the user back to `/`.
    const canViewPurchaseOrders = usePermission(PERMISSIONS.PURCHASE_ORDER_VIEW);
    const canViewSupplierBills = usePermission(PERMISSIONS.SUPPLIER_BILL_VIEW);
    const canViewSupplierPayments = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_VIEW);
    // /inventory/reports/tie-out compares stock value with the ledger -> same ACCOUNT_VIEW key as the financial statements.
    const canViewLedgerReports = usePermission(PERMISSIONS.ACCOUNT_VIEW);
    const canManageProcurementSettings = usePermission(PERMISSIONS.PROCUREMENT_SETTINGS_MANAGE);
    // 2026-10-17: store roles keep the procurement and sales keys their own tasks need (the Store
    // Manager PO approval band, packing and shipping challans, receiving customer returns) but reach
    // those tasks from queues on the store dashboard, not from the Procurement / Sales menus. A store
    // role keeps a menu only when it also owns work there (Store Admin raises POs, so it keeps
    // Procurement). Holders of no store key are untouched.
    const canIssueRequisitions = usePermission(PERMISSIONS.REQUISITION_ISSUE);
    const canIssueStock = usePermission(PERMISSIONS.STOCK_ISSUE);
    const canManagePurchaseOrders = usePermission(PERMISSIONS.PURCHASE_ORDER_MANAGE);
    const canManageSalesOrders = usePermission(PERMISSIONS.SALES_ORDER_MANAGE);
    const isStoreRole = canAccessStores || canIssueRequisitions || canIssueStock;
    // 2026-10-17: maintenance roles hold SUPPLIER_VIEW (external-repair vendor picker) and
    // FIXED_ASSET_VIEW (asset-link picker) for the work-order form only. They keep the keys but
    // not the Procurement / Fixed Assets menus, unless they also own work there.
    const holdsMaintenanceWoWork = usePermission(PERMISSIONS.MAINTENANCE_WO_WORK);
    const holdsMaintenanceWoManage = usePermission(PERMISSIONS.MAINTENANCE_WO_MANAGE);
    const isMaintenanceRole = holdsMaintenanceWoWork || holdsMaintenanceWoManage;
    const canManageFixedAssets = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE);
    const canRunDepreciation = usePermission(PERMISSIONS.FIXED_ASSET_DEPRECIATION_RUN);
    const canAccessFixedAssets = canViewFixedAssets
        && (!isMaintenanceRole || canManageFixedAssets || canRunDepreciation);
    const showProcurementMenu = (!isStoreRole && !isMaintenanceRole)
        || canManagePurchaseOrders || canManageSuppliers || canViewSupplierBills || canViewSupplierPayments;
    const canAccessProcurement = showProcurementMenu && (isProcurementManager || canAccessCatalogue || canViewSuppliers
        || canManageSuppliers || canViewPurchaseOrders || canViewSupplierBills || canViewSupplierPayments);

    const canAccessCustomers = usePermission(PERMISSIONS.CUSTOMER_VIEW);
    const canAccessQuotations = usePermission(PERMISSIONS.QUOTATION_VIEW);
    const canAccessSalesOrders = usePermission(PERMISSIONS.SALES_ORDER_VIEW); // W3-O
    const canManageSalesTeams = usePermission(PERMISSIONS.SALES_TEAM_MANAGE); // S0
    const canViewSalesTargets = usePermission(PERMISSIONS.SALES_TARGET_VIEW); // S2
    const canViewStockPeriod = usePermission(PERMISSIONS.STOCK_VIEW); // B4-D
    const canViewStockReservations = usePermission(PERMISSIONS.STOCK_RESERVATION_VIEW); // buying-house D1
    const canAccessSalesInvoices = usePermission(PERMISSIONS.SALES_INVOICE_VIEW);
    const canAccessSalesReports = usePermission(PERMISSIONS.SALES_REPORT_VIEW);
    const canAccessSalesReceipts = usePermission(PERMISSIONS.RECEIPT_VIEW);
    const canAccessSalesSettings = usePermission(PERMISSIONS.SALES_SETTINGS_VIEW);
    // ---- P1-B portal ----
    const canPortalManageKey = usePermission(PERMISSIONS.PORTAL_MANAGE);
    const canSalesSettingsManageKey = usePermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
    const canManagePortal = canPortalManageKey && canSalesSettingsManageKey;
    // ---- end P1-B ----
    const canAccessSalesBranding = usePermission(PERMISSIONS.SALES_BRANDING_VIEW);
    // MENU_SALES is held by no legacy role (admin wildcard only), which would collapse the group to
    // admin-only. The group must stay visible to anyone who can see any item inside it — same
    // pattern as canAccessShifts / canAccessLeave below.
    // Store roles: see the procurement note above. Sales menu only when the store role also does sales work.
    const showSalesMenu = !isStoreRole || canManageSalesOrders || canAccessCustomers || canAccessQuotations
        || canAccessSalesInvoices || canAccessSalesReceipts;
    const canAccessSales = showSalesMenu && (canAccessCustomers || canAccessQuotations || canAccessSalesInvoices
        || canAccessSalesReports || canAccessSalesSettings || canAccessSalesBranding || canAccessSalesOrders || canManageSalesTeams || canViewSalesTargets);
    const canAccessCrm = usePermission(PERMISSIONS.CRM_VIEW);

    // T9: Commercial (export LC / back-to-back LC / amendments / checklist). R-1: GATED (unlike
    // insurance above), via gatedModule: 'commercial' on the catalog card, checked against
    // enabledModules — never against role holdings alone.
    const canAccessLc = usePermission(PERMISSIONS.LC_VIEW);

    // Fix round 1: /payroll/batches has its own key — see the 'payroll.batches' gate below.
    const canAccessSalaryPayments = usePermission(PERMISSIONS.SALARY_PAYMENT_MANAGE);
    // Task-8 review F1: EmployeeExitController guards its list route with USER_VIEW (spec
    // §2.4/M6 — deliberate), not PAYROLL_VIEW/MANAGE, so /payroll/exits has its own key — see the
    // 'payroll.exits' gate below.
    const canAccessEmployeeExits = usePermission(PERMISSIONS.USER_VIEW);

    // T3a (piece-rate garments completion plan, 2026-09-09): reachable by anyone who can see the
    // catalog/entries respectively -- mirrors the backend GET guards' `or` chains
    // (PieceRateOperationController#getByInstitution, ProductionEntryController#listForUser).
    // Each usePermission is hoisted unconditionally (rules-of-hooks: a hook after `||`
    // short-circuits and becomes conditional), then combined.
    const holdsPieceRateView = usePermission(PERMISSIONS.PIECE_RATE_VIEW);
    const holdsPieceRateManage = usePermission(PERMISSIONS.PIECE_RATE_MANAGE);
    const holdsProductionEntryRecord = usePermission(PERMISSIONS.PRODUCTION_ENTRY_RECORD);
    const holdsProductionEntryApprove = usePermission(PERMISSIONS.PRODUCTION_ENTRY_APPROVE);
    const canAccessPieceRateOperations = holdsPieceRateView || holdsPieceRateManage
        || holdsProductionEntryRecord || holdsProductionEntryApprove;
    const canAccessProductionEntries = holdsPieceRateView
        || holdsProductionEntryRecord || holdsProductionEntryApprove;

    const canAccessExpenseCategories = usePermission(PERMISSIONS.EXPENSE_CATEGORY_VIEW);
    const canAccessExpensePolicy = usePermission(PERMISSIONS.EXPENSE_POLICY_VIEW);
    const canAccessAllExpenseClaims = usePermission(PERMISSIONS.EXPENSE_CLAIM_VIEW_ALL);
    const canAccessExpenseAdvanceApprovals = usePermission(PERMISSIONS.EXPENSE_ADVANCE_VIEW_ALL);

    const canAccessShiftList = usePermission(PERMISSIONS.SUBMENU_SHIFT_LIST);
    const canAccessShiftAssignments = usePermission(PERMISSIONS.SUBMENU_SHIFT_ASSIGNMENTS);
    const canAccessShiftRotation = usePermission(PERMISSIONS.SUBMENU_SHIFT_ROTATION_PATTERNS);
    const canAccessShiftRoster = usePermission(PERMISSIONS.SUBMENU_SHIFT_ROSTER);
    const canAccessShiftOrphans = usePermission(PERMISSIONS.SUBMENU_SHIFT_ORPHANS);
    const canAccessShifts = usePermission(PERMISSIONS.MENU_SHIFTS) || canAccessShiftList || canAccessShiftAssignments
        || canAccessShiftRotation || canAccessShiftRoster || canAccessShiftOrphans;

    const canSeeLeaveAllApplications = usePermission(PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS);
    const canSeeLeaveBalances = usePermission(PERMISSIONS.SUBMENU_LEAVE_BALANCES);
    const canSeeLeaveTypes = usePermission(PERMISSIONS.SUBMENU_LEAVE_TYPES);
    const canSeeLeavePolicy = usePermission(PERMISSIONS.SUBMENU_LEAVE_POLICY);
    const canSeeLeaveAllocations = usePermission(PERMISSIONS.SUBMENU_LEAVE_ALLOCATIONS);
    const canAccessLeave = usePermission(PERMISSIONS.MENU_LEAVE) || canSeeLeaveAllApplications || canSeeLeaveBalances
        || canSeeLeaveTypes || canSeeLeavePolicy || canSeeLeaveAllocations;

    const canAccessPendingApprovalRequisition = usePermission(PERMISSIONS.REQUISITION_APPROVE);
    const canAccessPendingIssueRequisition = usePermission(PERMISSIONS.REQUISITION_ISSUE);

    // Merchandising (garments-merchandising T7) — the card gate is MENU_MERCHANDISING; each of the
    // six links carries its own *_VIEW gate, matching the sales-card fix-round-1 pattern above so a
    // role holding only one sub-permission never sees a link it cannot open.
    const canAccessMerchandising = usePermission(PERMISSIONS.MENU_MERCHANDISING);
    const canAccessBuyers = usePermission(PERMISSIONS.BUYER_VIEW);
    const canAccessStyles = usePermission(PERMISSIONS.STYLE_VIEW);
    const canAccessSampleRequests = usePermission(PERMISSIONS.SAMPLE_REQUEST_VIEW);
    const canAccessBuyerOrders = usePermission(PERMISSIONS.BUYER_ORDER_VIEW);
    const canAccessMerchReports = usePermission(PERMISSIONS.MERCH_REPORT_VIEW);
    // Commercial / customs (T10): its own GATED module card (`gatedModule: 'commercial'`, R-1),
    // keyed on MENU_COMMERCIAL plus a union of the sub-page view keys so a role holding e.g. only
    // UD_VIEW still sees the card (same pattern as canAccessShifts/canAccessLeave above).
    const canAccessUdRegister = usePermission(PERMISSIONS.UD_VIEW);
    const canAccessExpForm = usePermission(PERMISSIONS.EXP_VIEW);
    const canAccessCashIncentive = usePermission(PERMISSIONS.CASH_INCENTIVE_VIEW);
    const canAccessQc = usePermission(PERMISSIONS.QC_INSPECTION_VIEW);
    const canAccessAgentCommission = usePermission(PERMISSIONS.AGENT_COMMISSION_VIEW);
    const canAccessCommercialSettings = usePermission(PERMISSIONS.COMMERCIAL_SETTINGS_VIEW);
    // Maintenance (L0): its own GATED module card (`gatedModule: 'maintenance'`); each sub-page gates on its own key.
    const canAccessMaintenanceEquipment = usePermission(PERMISSIONS.MAINTENANCE_EQUIPMENT_VIEW);
    const canManageMaintenanceSettings = usePermission(PERMISSIONS.MAINTENANCE_SETTINGS_MANAGE);
    // L0 ships only the equipment register and settings pages, so the card opens only for a holder of
    // one of those keys: a role with just a work-order/PM/reading key would land on a page it cannot
    // open. Lanes L1-L4 widen this as their pages (and links) arrive.
    const canRecordMaintenanceReadings = usePermission(PERMISSIONS.MAINTENANCE_READING_RECORD); // L2
    // L1 adds the triage inbox and the work-order list; a technician holding only the work key opens the card too.
    const canTriageMaintenance = usePermission(PERMISSIONS.MAINTENANCE_REQUEST_TRIAGE);
    const canViewAllWorkOrders = usePermission(PERMISSIONS.MAINTENANCE_WO_VIEW_ALL);
    const canWorkOrders = usePermission(PERMISSIONS.MAINTENANCE_WO_WORK);
    const canManageWorkOrders = usePermission(PERMISSIONS.MAINTENANCE_WO_MANAGE);
    const canAccessMaintenanceWorkOrders = canViewAllWorkOrders || canWorkOrders;
    const canViewMaintenancePm = usePermission(PERMISSIONS.MAINTENANCE_PM_VIEW); // L3
    const canViewMaintenanceReports = usePermission(PERMISSIONS.MAINTENANCE_REPORT_VIEW); // L5
    const canAccessMaintenance = canAccessMaintenanceEquipment || canManageMaintenanceSettings
        || canTriageMaintenance || canAccessMaintenanceWorkOrders || canManageWorkOrders || canRecordMaintenanceReadings
        || canViewMaintenancePm || canViewMaintenanceReports;
    const canViewIndirectTax = usePermission(PERMISSIONS.INDIRECT_TAX_VIEW);
    const canViewCompliance = usePermission(PERMISSIONS.COMPLIANCE_VIEW); // W3-R
    const canViewIntercompany = usePermission(PERMISSIONS.INTERCOMPANY_VIEW); // W3-M
    const canViewAuditLog = usePermission(PERMISSIONS.AUDIT_LOG_VIEW); // W3-K
    const canViewReportsForStatements = usePermission(PERMISSIONS.FINANCIAL_REPORT_VIEW); // W3-K
    const canViewAccountsForStatements = usePermission(PERMISSIONS.ACCOUNT_VIEW); // W3-K
    const canViewProjects = usePermission(PERMISSIONS.PROJECT_VIEW); // P1-A
    // ---- W3-K journal-import/tally ----
    const canManageJournalsForImport = usePermission(PERMISSIONS.JOURNAL_ENTRY_MANAGE);
    const canViewReportsForTally = usePermission(PERMISSIONS.FINANCIAL_REPORT_VIEW);
    const canViewAccountsForTally = usePermission(PERMISSIONS.ACCOUNT_VIEW);
    // ---- end W3-K journal-import/tally ----
    // ---- W3-V FP&A ----
    const canViewBudgets = usePermission(PERMISSIONS.BUDGET_VIEW);
    const canViewReportsForFpa = usePermission(PERMISSIONS.FINANCIAL_REPORT_VIEW);
    const canViewCostingForFpa = usePermission(PERMISSIONS.COSTING_VIEW);
    // ---- end W3-V ----
    // ---- W3-X data in/out ----
    const canManageCustomersForImport = usePermission(PERMISSIONS.CUSTOMER_MANAGE);
    const canManageSuppliersForImport = usePermission(PERMISSIONS.SUPPLIER_MANAGE);
    const canManageItemsForImport = usePermission(PERMISSIONS.ITEM_MANAGE);
    const canManageAccountsForImport = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const canManageIntegrations = usePermission(PERMISSIONS.INTEGRATION_MANAGE);
    const canManageTransactions = usePermission(PERMISSIONS.TRANSACTION_MANAGE);   // F11
    const canManageCashbookAccess = usePermission(PERMISSIONS.CASHBOOK_ACCESS_MANAGE);   // F11
    // ---- end W3-X ----
    const canViewTaxForRemit = usePermission(PERMISSIONS.TAX_VIEW); // W3-R
    const canViewPayrollForRemit = usePermission(PERMISSIONS.PAYROLL_VIEW); // W3-R
    // T11: the three report pages share one key, COMMERCIAL_REPORT_VIEW.
    const canAccessCommercialReport = usePermission(PERMISSIONS.COMMERCIAL_REPORT_VIEW);
    // T9+T10+T11 merge: one gate over every commercial surface (LC + UD/EXP/incentive/settings/reports).
    const canAccessCommercial = usePermission(PERMISSIONS.MENU_COMMERCIAL) || canAccessLc
        || canAccessUdRegister || canAccessExpForm || canAccessCashIncentive
        || canAccessCommercialSettings || canAccessCommercialReport;

    const visibility: Record<string, boolean> = {
        // --- module cards ---
        attendance: canAccessAttendance,
        shifts: canAccessShifts,
        leave: canAccessLeave,
        holidays: canAccessHolidays,
        // Self-service: any logged-in institution member sees their own payslips (server scopes to caller).
        myPayslips: true,
        myBreakdowns: true, // Maintenance L1: any employee may report a breakdown (the module gate still applies)
        myProvidentFund: true, // W3-J
        users: canAccessUsers,
        // Roles administration: the legacy sidebar gated /roles on isAdmin alone, not on a key.
        roles: isAdmin,
        employeeDocuments: canAccessUsers,
        departments: canAccessDepartments,
        stores: canAccessStores,
        items: canAccessItemManagement,
        stocktakes: canAccessStocktakes,
        lowStock: canAccessLowStock,
        lotRegister: canAccessLowStock, // the lot search and export endpoints need STOCK_MANAGE
        stockTransfers: canAccessStockTransfers,
        requisitions: canAccessRequisitions,
        procurement: canAccessProcurement,
        accounting: canAccessAccounting,
        banking: canAccessBanking,
        fixedAssets: canAccessFixedAssets,
        financing: canAccessFinancing, // W3-A
        costing: canAccessCosting, // W3-I
        payroll: canAccessPayroll,
        insurance: canAccessInsurance,
        // The Expense section was rendered unconditionally for every authenticated user (the
        // self-service My Claims / My Advances / Pending Approval pages have no gate at all).
        // MENU_EXPENSE is held only by the two NEW templates (HR Manager, Department Head) plus the
        // admin wildcard, so gating on it would strip every legacy role's self-service links.
        expense: true,
        tax: canAccessTax,
        sales: canAccessSales,
        crm: canAccessCrm,
        merchandising: canAccessMerchandising,
        commercial: canAccessCommercial,
        maintenance: canAccessMaintenance,
        dashboards: canAccessDashboard,
        reports: canAccessReports,
        tasks: canAccessTasks,
        notices: canAccessNotices,
        notifications: true, // W3-S: every member has an inbox
        deviceUsers: canAccessDevices,
        apiKeys: canAccessApiKeys,
        billing: canAccessBilling,
        institutionSettings: canAccessInstitutionSettings,
        institutions: isSystemAdmin,
        saasOperator: isSaaSOperator || isSystemAdmin,

        // --- pages inside a module ---
        'attendance.admin': isAttendanceBoardRole,
        'attendance.sites': canManageWorkSites,
        'shifts.list': canAccessShiftList,
        'shifts.assignments': canAccessShiftAssignments,
        'shifts.rotation': canAccessShiftRotation,
        'shifts.roster': canAccessShiftRoster,
        'shifts.orphans': canAccessShiftOrphans,
        'leave.allApplications': canSeeLeaveAllApplications,
        'leave.types': canSeeLeaveTypes,
        'leave.policy': canSeeLeavePolicy,
        'leave.allocations': canSeeLeaveAllocations,
        'leave.balances': canSeeLeaveBalances,
        'items.uom': canAccessUom,
        'requisitions.pendingApproval': canAccessPendingApprovalRequisition,
        'requisitions.pendingIssue': canAccessPendingIssueRequisition,
        'procurement.adminOrManager': showProcurementMenu && isProcurementManager,
        'procurement.purchaseOrders': showProcurementMenu && canViewPurchaseOrders,
        'procurement.pendingApproval': showProcurementMenu && (isProcurementManager && canViewPurchaseOrders),
        'procurement.bills': showProcurementMenu && canViewSupplierBills,
        'inventory.tieOut': canViewLedgerReports,
        'procurement.payments': showProcurementMenu && canViewSupplierPayments,
        'procurement.settings': showProcurementMenu && canManageProcurementSettings,
        'procurement.suppliers': showProcurementMenu && canAccessSuppliers,
        // /procurement/catalogue sits under the PURCHASE_ORDER_VIEW route guard.
        'procurement.catalogue': showProcurementMenu && (canAccessCatalogue && canViewPurchaseOrders),
        // Claim approval authority is now per-tier ROLE (expense_policy_tier.approver_role_id), which
        // the client cannot resolve synchronously, so there is no single key for "can approve".
        // Show the queue to admins and to anyone holding a claim/advance/policy key -- the roles
        // that are approvers in practice. Others can still reach /expense/pending-approval by link;
        // the server returns only the claims they can act on.
        'expense.approver': isAdmin || canAccessAllExpenseClaims || canAccessExpenseAdvanceApprovals || canAccessExpensePolicy,
        'expense.finance': canAccessAllExpenseClaims,
        // Fix round 1: the advance-approval queue is keyed EXPENSE_ADVANCE_VIEW_ALL, as it was
        // pre-merge — not the claim key. No legacy role diverges (Accountant holds both), but a
        // dynamic role granted only one of the pair must get exactly the page that key buys.
        'expense.advances': canAccessExpenseAdvanceApprovals,
        // Fix round 1: split back apart. Folding these into one `expense.admin` gate widened each
        // link to the union of the two keys for any dynamic role holding only one of them.
        'expense.categories': canAccessExpenseCategories,
        'expense.policy': canAccessExpensePolicy,
        // Fix round 1: every Sales sub-link carries its own gate again, matching the route guards
        // in App.tsx. Without these, a SalesRep (CUSTOMER_VIEW + QUOTATION_VIEW, and no invoice,
        // settings or report key) saw four links that bounce straight back to "/".
        'sales.customers': showSalesMenu && canAccessCustomers,
        'sales.quotations': showSalesMenu && canAccessQuotations,
        'sales.orders': showSalesMenu && canAccessSalesOrders, // W3-O
        'sales.teams': showSalesMenu && canManageSalesTeams, // S0
        'sales.targets': showSalesMenu && canViewSalesTargets, // S2
        'inventory.stockPeriod': canViewStockPeriod, // B4-D
        'inventory.stockReservations': canViewStockReservations, // buying-house D1
        'sales.invoices': showSalesMenu && canAccessSalesInvoices,
        'sales.settings': showSalesMenu && canAccessSalesSettings,
        'sales.portal': showSalesMenu && canManagePortal, // P1-B
        'sales.reports': showSalesMenu && canAccessSalesReports,
        'sales.receipts': showSalesMenu && canAccessSalesReceipts,
        'sales.branding': showSalesMenu && canAccessSalesBranding,
        'fixedAssets.settings': canManageFixedAssetSettings,
        'fixedAssets.tieOut': canViewReportsForStatements, // returns ledger balances: the books' report key
        // "My Asset Requests" is open to every member, but only while the Fixed Assets module is on
        'fixedAssets.requests': enabledModules.has('fixed_assets'),
        'maintenance.equipment': canAccessMaintenanceEquipment,
        'maintenance.settings': canManageMaintenanceSettings,
        'maintenance.readings': canRecordMaintenanceReadings,
        'maintenance.requests': canTriageMaintenance,
        'maintenance.workOrders': canAccessMaintenanceWorkOrders || canManageWorkOrders,
        'maintenance.pm': canViewMaintenancePm,
        'maintenance.dashboard': canViewAllWorkOrders,
        'maintenance.myWork': canWorkOrders,
        // L5: a meter reader (READING_RECORD only) reaches just the utility reports, via the same page.
        'maintenance.reports': canViewMaintenanceReports || canRecordMaintenanceReadings,
        'commercial.lc': canAccessLc,
        // Commercial party master (Task 4a, spec §2, 7.4) -- same predicate as 'commercial.lc'
        // above (CommercialPartyController's @authz.can guards reuse LC_VIEW/LC_MANAGE too).
        'commercial.parties': canAccessLc,
        // Fix round 1: the payment-batch screen is SALARY_PAYMENT_MANAGE at both the route
        // (App.tsx) and the controller; inheriting the Payroll card's MENU_PAYROLL gave any role
        // holding the menu key but not the payment key a dead link.
        'payroll.batches': canAccessSalaryPayments,
        // Task-8 review F1: EmployeeExitController's list route is USER_VIEW, not
        // MENU_PAYROLL/PAYROLL_VIEW — inheriting the Payroll card's gate alone gave any role
        // holding MENU_PAYROLL but not USER_VIEW a link that 403s on load.
        'payroll.exits': canAccessEmployeeExits,
        'merchandising.dashboard': canAccessMerchReports,
        'merchandising.buyers': canAccessBuyers,
        'merchandising.styles': canAccessStyles,
        'merchandising.samples': canAccessSampleRequests,
        'merchandising.orders': canAccessBuyerOrders,
        'merchandising.orderBook': canAccessMerchReports,
        'merchandising.sampleTracker': canAccessMerchReports,
        'merchandising.orderDashboard': canAccessMerchReports,
        'merchandising.sampleWorkload': canAccessMerchReports,
        'merchandising.qc': canAccessQc,
        // Fix round 1 / H1: these two gate keys were computed above but never wired into the
        // returned map, so `visibility[link.gate]` resolved to `undefined` (never `true`) and
        // moduleCatalog.ts's link filter (`!link.gate || visibility[link.gate] === true`) dropped
        // both links unconditionally, for every role, regardless of permission.
        'payroll.pieceRateOperations': canAccessPieceRateOperations,
        'payroll.productionEntries': canAccessProductionEntries,
        // Platform-maintenance tool: SystemAdmin only (route stays).
        'accounting.openingBalances': canManageAccountsForOpening,
        'accounting.groupBalances': canReclassGroupBalances && isSystemAdmin,
        // F11 review: the Transactions page is now the cash in / out form, so cashiers (TRANSACTION_MANAGE) get the link too.
        'accounting.cashTransactions': isSystemAdmin || canManageTransactions,
        'accounting.indirectTax': canViewIndirectTax,
        'tasks.assign': canAssignTasks,
        'notices.manage': canManageNotices,
        'notifications.rules': canManageReminderRules, // W3-S
        'approvals.workflows': canManageApprovalWorkflows, // FW-3A
        'accounting.compliance': canViewCompliance, // W3-R
        'accounting.intercompany': canViewIntercompany, // W3-M
        'accounting.auditLog': canViewAuditLog, // W3-K
        'accounting.statements': canViewReportsForStatements && canViewAccountsForStatements, // W3-K
        'accounting.projects': canViewProjects, // P1-A
        'accounting.journalImport': canManageJournalsForImport, // W3-K
        'accounting.tallyExport': canViewReportsForTally && canViewAccountsForTally, // W3-K
        'accounting.budgetVersions': canViewBudgets, // W3-V
        'accounting.fpaReports': canViewReportsForFpa, // W3-V
        'accounting.buyerProfitability': canViewReportsForFpa && canViewCostingForFpa, // W3-V
        'accounting.dataImport': canManageCustomersForImport || canManageSuppliersForImport || canManageItemsForImport || canManageAccountsForImport || canManageTransactions, // W3-X; F11 cash transactions
        'accounting.integrations': canManageIntegrations, // W3-X
        'accounting.txnMappingRules': canManageTransactions, // F11 (11c)
        'banking.cashbookAccess': canManageCashbookAccess, // F11 (11b)
        'payroll.statutoryRemittances': canViewTaxForRemit || canViewPayrollForRemit, // W3-R
        'dashboard.attendance': isDashboardBoardRole && visibleModules.has('attendance'),
        'dashboard.leave': isDashboardBoardRole && visibleModules.has('leave'),
        'dashboard.people': isDashboardBoardRole && visibleModules.has('people'),
        'dashboard.accounting': isBusinessBoardRole && visibleModules.has('accounting'),
        'dashboard.inventory': isBusinessBoardRole && visibleModules.has('inventory'),
        'dashboard.sales': isBusinessBoardRole && visibleModules.has('sales'),
        'dashboard.payroll': isBusinessBoardRole && visibleModules.has('payroll'),
        'dashboard.expense': isBusinessBoardRole && visibleModules.has('expense'),
        'dashboard.roleDefaults': canManageAllBoards || (canManageTeamBoards && teamHasTargets),
        'dashboard.teamBoards': canManageAllBoards || canManageTeamBoards,
        'commercial.ud': canAccessUdRegister,
        'commercial.exp': canAccessExpForm,
        'commercial.incentives': canAccessCashIncentive,
        'commercial.settings': canAccessCommercialSettings,
        'commercial.agentCommission': canAccessAgentCommission,
        'commercial.reports': canAccessCommercialReport,
        // Shipping & export docs (15.2/15.3) — reuses the EXP_VIEW predicate (spec §3, no new
        // permission catalog entry): a commercial officer who manages EXP forms manages their
        // shipping docs too.
        'commercial.shipping': canAccessExpForm,
    };

    return { visibility, enabledModules };
};
